import type { ReactNode } from "react";
import { Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  memberLetter,
  type BlockMember,
  type IntensityKind,
} from "@heraion/shared";

import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import { ICON_HIT } from "./composerSetTableShared";

// One member's head row inside ComposerSetTable's metric table: A/B frame,
// name, kind selector, 📝 exercise note, ⇄/✕. Split out of ComposerSetTable
// for size (SP-COMPOSERSETTABLE) — this is a pure move, so the state it reads
// stays owned by the table and arrives as props.
//
// `instructionsOpen` is deliberately a BOOLEAN, not the table's
// `instrOpen: number | null`: only the table knows which member is open, and
// handing the raw index down would let this component answer that question a
// second way. Same for the note toggle, which arrives already bound to this
// member — so there is no path where the head row can open a DIFFERENT
// member's editor.
export function ComposerMemberHead({
  member,
  idx,
  grouped,
  headerKind,
  instructionsOpen,
  onToggleInstructions,
  onSetInstructions,
  onReplaceMember,
  onRemoveMember,
  renderKindSelect,
  styles,
  theme,
}: {
  member: BlockMember;
  idx: number;
  grouped: boolean;
  headerKind: IntensityKind;
  instructionsOpen: boolean;
  onToggleInstructions: () => void;
  onSetInstructions: (text: string) => void;
  onReplaceMember: () => void;
  onRemoveMember: () => void;
  // Owned by the table: it holds the kind labels and the onSetKind callback,
  // and the solo header renders the very same selector at the table head.
  renderKindSelect: (memberIdx: number, kind: IntensityKind) => ReactNode;
  styles: ComposerStyles;
  theme: UiTheme;
}) {
  const { t } = useTranslation();

  return (
    <View style={{ gap: 6 }}>
      <View style={styles.memberHead}>
        {/* Plain letter, the logger's GroupMemberHeader convention —
             matches the row labels (member B owns rows 1B, 2B…). */}
        {grouped ? (
          <Text style={styles.memberOrdinal}>{memberLetter(idx)}</Text>
        ) : null}
        <Text style={styles.memberName} numberOfLines={1}>
          {member.exerciseName ?? member.exerciseId}
        </Text>
        {grouped ? (
          <View style={styles.headKindCol}>
            {renderKindSelect(
              idx,
              member.setRows[0]?.intensityKind ?? headerKind,
            )}
          </View>
        ) : null}
        {/* Disclosure toggle for the exercise note. `active`/`expanded`
            track OPEN (the field appears below, so the on-state is never
            colour-only); the icon SHAPE carries has-content — a filled
            `note` when written, an `edit` pencil ("add a note") when empty,
            so that cue survives colour-blindness too. */}
        <IconButton
          theme={theme}
          name={member.instructions.trim() !== "" ? "note" : "edit"}
          active={instructionsOpen}
          expanded={instructionsOpen}
          accessibilityLabel={t("composer.exerciseNote", {
            defaultValue: "Exercise note",
          })}
          onPress={onToggleInstructions}
          hitSlop={ICON_HIT}
        />
        <IconButton
          theme={theme}
          name="arrow-left-right"
          accessibilityLabel={t("composer.replaceExercise", {
            defaultValue: "Replace exercise",
          })}
          onPress={onReplaceMember}
          hitSlop={ICON_HIT}
        />
        <IconButton
          theme={theme}
          name="close"
          variant="destructive"
          accessibilityLabel={t("composer.removeExercise", {
            defaultValue: "Remove exercise",
          })}
          onPress={onRemoveMember}
          hitSlop={ICON_HIT}
        />
      </View>
      {/* Per-EXERCISE note (slot instructions): what the logger shows
           as the entry's 💬 note. */}
      {instructionsOpen ? (
        <TextInput
          style={styles.noteInput}
          value={member.instructions}
          onChangeText={onSetInstructions}
          placeholder={t("composer.exerciseNotePlaceholder", {
            defaultValue: "Note for this exercise (shown while logging)",
          })}
          maxLength={500}
          accessibilityLabel={t("composer.exerciseNote", {
            defaultValue: "Exercise note",
          })}
        />
      ) : null}
    </View>
  );
}
